import { useMemo, useState, useEffect, useRef } from "react";

// 위험성 평가 커스텀 훅
import { useGetTableDataInQueryString } from "../../RISK_ASSESSMENT/components/Table/hooks";

import dayjs from "dayjs";
import * as echarts from 'echarts'; // ECharts 3.0.2 호환
import { AgGridReact } from "ag-grid-react";
import myTheme from "../../common/DataGrid/ag-grid-theme-builder";

import {
  alpha,
  Box, Button,
  Card, CardContent, Collapse,
  Drawer, Divider, Dialog, DialogContent, DialogActions,
  Grid,
  IconButton, InputAdornment,
  OutlinedInput,
  Typography, Tooltip, TextField,
  useTheme, useMediaQuery,
} from "@mui/material";
import { styled } from '@mui/material/styles';

import EqualizerIcon from '@mui/icons-material/Equalizer';
import InsightsIcon from '@mui/icons-material/Insights';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { riskLevelMapper } from "../../RISK_ASSESSMENT/constants/columns"
import { FONT_PROPS } from "../../../styles/colors";

//////////////////////////////////
// 스타일
//////////////////////////////////
const SquareIconButtonBase = styled(IconButton)(({ theme }) => ({
  width: 32,
  height: 32,
  padding: 0,
  borderRadius: 4,
  border: `1px solid ${alpha('#111111', 0.80)}`,
  color: '#fff',
  boxShadow: theme.shadows[2],
  '& .MuiSvgIcon-root': { fontSize: 24, transition: 'transform .2s' },
  '&&:hover': { boxShadow: theme.shadows[3] },
}));
const SquareIconButtonToggle = styled(SquareIconButtonBase, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  backgroundColor: selected ? theme.palette.primary.main : '#111111',
  '&&:hover': {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : alpha('#303030', 1),
  },
}));
const SquareIconButton = styled(SquareIconButtonBase)(({ theme }) => ({
  backgroundColor: '#111111',
  '&&:hover': { backgroundColor: alpha('#303030', 1) },
  '&:active': { transform: 'scale(0.96)' },            // 클릭 피드백
  '&.Mui-focusVisible': { outline: `2px solid ${alpha('#fff', 0.35)}` }, // 접근성
}));

/*
const SquareIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  width: 32,
  height: 32,
  padding: 0,
  borderRadius: 4, // 네모(살짝 둥글게)
  backgroundColor: selected
    ? theme.palette.primary.main   // 선택 상태 배경
    : '#111111',                 // 일반 배경
  border: `1px solid ${alpha('#111111', 0.80)}`,
  color: '#fff',
  '&&:hover': {
    backgroundColor: selected ? theme.palette.primary.dark : alpha('#303030', 1),
  },
  '& .MuiSvgIcon-root': { fontSize: 24 },
  // '&.Mui-disabled': {
  //   backgroundColor: theme.palette.action.disabledBackground,
  //   color: theme.palette.action.disabled,
  // },
}));
*/

///////////////////////////////////
// 위험 지표 컴포넌트
//////////////////////////////////
export function RiskIndicators({ 
  //isAvailable = false,
  isComplexUI = false,   // 복합 UI 모드 여부  
  group_code,
  project_code,
  referenceDate,
}) {
  const theme = useTheme();

  const LEFT_W = 80; // 좌측 사이드바 너비
  const RIGHT_W = isComplexUI ? 350 : 0; // 우측 상세정보 창 너비
  const DRAWER_HEIGHT = 280;
  const DRAWER_GAP = 4; // 드로어와 버튼 사이 간격

  // 지표 활성화 여부
  const [isActive, setIsActive] = useState(true);

  // 렌더링 상태 관리 
  const RenderState = Object.freeze({
    STRATEGY: "strategyMode", // 개선 대책 모드
    EVALUATION: "evaluationMode", // 위험성 평가 모드
  });
  const [mode, setMode] = useState(RenderState.STRATEGY);
  const changeModeState = (newState) => setMode(newState);

  return (
    <>
      <DrawerToggleButton
        isOpen={isActive}
        onToggle={() => setIsActive((v) => !v)}
        leftWidth={LEFT_W}
        rightWidth={RIGHT_W}
        ariaControlsId="year-metrics-drawer"
        drawerHeight={DRAWER_HEIGHT}
        drawerGap={DRAWER_GAP}
      />
      <Drawer
        anchor="bottom"
        variant="persistent"
        open={isActive}
        ModalProps={{
          keepMounted: true,
          hideBackdrop: true,
        }}
        sx={{
          "& .MuiDrawer-paper": {
            height: DRAWER_HEIGHT,
            overflow: 'visible',                 // 중요!
            bgcolor: alpha("#111111", 0.5),
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            border: `1px solid ${alpha("#111111", 0.20)}`,
            left: LEFT_W,
            right: RIGHT_W,
            width: `calc(100vw - ${LEFT_W + RIGHT_W}px)`,
          },
        }}
      >
        {/** 선택 버튼 */}
        <Box sx={{ position: 'absolute', top: -38, left: 8, zIndex: 1, display: 'flex', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: alpha("#111111", 0.8), border: `1px solid ${alpha("#111111", 0.20)}`,color: '#fff', borderRadius: 1, px: 2, py: 0.5 }}>
            { dayjs(referenceDate).format('YYYY') }년 지표
          </Box>
          <Tooltip title="개선대책 결과 지표" arrow placement="top">
            <SquareIconButtonToggle 
              aria-label="개선대책 결과 지표" 
              selected={mode === RenderState.STRATEGY}
              onClick={(e) => {
                e.stopPropagation();
                changeModeState(RenderState.STRATEGY);
              }}
            >
              <EqualizerIcon />
            </SquareIconButtonToggle>
          </Tooltip>
          <Tooltip title="위험성 평가 결과 지표" arrow placement="top">
            <SquareIconButtonToggle
              aria-label="위험성 평가 결과 지표"
              selected={mode === RenderState.EVALUATION}
              onClick={(e) => {
                e.stopPropagation();
                changeModeState(RenderState.EVALUATION);
              }}
            >
              <InsightsIcon />
            </SquareIconButtonToggle>
          </Tooltip>
        </Box>
        {/** 지표 */}
        {mode === RenderState.STRATEGY && <StrategyView group_code={group_code} project_code={project_code} referenceDate={referenceDate} />}
        {mode === RenderState.EVALUATION && <EvaluationView group_code={group_code} project_code={project_code} referenceDate={referenceDate} />}
      </Drawer>
    </>
  );
}

// 위험성 평가 결과 지표
const EvaluationView = ({ group_code, project_code, referenceDate }) => {
  const theme = useTheme();

  // 지표 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getEvaluationResultData",
    group_code,
    project_code,
    { params: { date: referenceDate } },
  );

  const [tempGetData, setTempGetData] = useState([]);
  // getData 변경될 때마다 상태 업데이트
  useEffect(() => {
    if(!getData.data || getData.data.length === 0) {
      return;
    }
    const tempData = { ...getData.data };
    setTempGetData(tempData);
  }, [getData.data]);

  // 선택한 평가연도의 유해위험요인 지표 데이터 가져오기
  const getSelectedYearRegisterCauseData = (year) => {
    const data = tempGetData.registerCauseData?.find(item => item.year === year);
    // TODO: null인 경우 표시 처리
    return data;
  };
  // 선택한 평가연도의 위험성 평가완료 지표 데이터 가져오기
  const getSelectedYearEvalData = (year) => {
    const data = tempGetData.evalResultData?.find(item => item.year === year);
    // TODO: null인 경우 표시 처리
    return data;
  };

  // 렌더링
  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', gap: 2, p: 1 }}>
      {/* 유해위험요인 건수 추이 차트 */}
      <Box sx={{ width: '100%', height: '100%'}}>
        <MonthlyTrendRegisterCauseChart 
          chartData={getSelectedYearRegisterCauseData(Number(dayjs(referenceDate).format('YYYY')))} 
        />
      </Box>
      {/* 유해위험요인 건수 3년 추이 */}
      <Box sx={{ width: '100%', height: '100%' }}>
        <Card
          sx={{ 
            width: '100%',
            height: '100%',
            border: `2px solid ${alpha("#111111", 0.20)}`,
            borderRadius: "0.375rem", 
            bgcolor: alpha("#111111", 0.5),
          }}
        >
          <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 13, mt: 1, ml: 1, color: '#ffffff' }}>
            유해위험요인 건수 3년 추이
          </Typography>
          <Box sx={{ height: 110 }}>
            <ThreeYearTrendRegisterCauseChart 
              chartData={getSelectedYearRegisterCauseData(Number(dayjs(referenceDate).format('YYYY')))} 
            />
          </Box>
          <Box display="flex" justifyContent="space-around" alignItems="center" flex={1}>
            <ThreeYearTrendRegisterCauseChart 
              chartData={getSelectedYearRegisterCauseData(Number(dayjs(referenceDate).format('YYYY')) - 2)} 
            />
            <ThreeYearTrendRegisterCauseChart 
              chartData={getSelectedYearRegisterCauseData(Number(dayjs(referenceDate).format('YYYY')) - 1)} 
            />
          </Box>
        </Card>
      </Box>
      {/** 월별 평가완료 지표 */}
      <Box sx={{ width: '100%', height: '100%' }}>
        <MonthlyEvaluationLevelGrid 
          cardData={getSelectedYearEvalData(Number(dayjs(referenceDate).format('YYYY')))} 
        />
      </Box>
      {/* 위험성 평가율 3년 추이 차트 */}
      <Box sx={{ width: '100%', height: '100%' }}>
        <Card sx={{ width: '100%',height: '100%',border: `2px solid ${alpha("#111111", 0.20)}`,borderRadius: "0.375rem", bgcolor: alpha("#111111", 0.5),}}>
          <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 13, color: '#ffffff', mt: 1, ml: 1, mb: 10 }}>
            위험성 평가율 3년 추이
          </Typography>
          <Box display="flex" justifyContent="space-around" alignItems="center" flex={1}>
            <EvaluationRateDonut 
              chartData={getSelectedYearEvalData(Number(dayjs(referenceDate).format('YYYY')) - 2)} 
            />
            <EvaluationRateDonut 
              chartData={getSelectedYearEvalData(Number(dayjs(referenceDate).format('YYYY')) - 1)} 
            />
            <EvaluationRateDonut 
              chartData={getSelectedYearEvalData(Number(dayjs(referenceDate).format('YYYY')))} 
            />
          </Box>
          {/* 범례 */}
          <Box mt={6} display="flex" justifyContent="center" alignItems="center" gap={2} >
            <Box width={10} height={10} borderRadius="50%" bgcolor="#6287DB" />
            <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 12, color: '#fff'}}>
              평가율
            </Typography>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};
// 월별 유해위험요인 건수 추이 차트
const MonthlyTrendRegisterCauseChart = ({ chartData }) => {
  const theme = useTheme();
  const chartRef = useRef(null);

  useEffect(() => {
    if(!chartData || chartData.length === 0) {
      return;
    }

    const myChart = echarts.init(chartRef.current);

    const option = {
      title: { // 타이틀 설정
        text: '월별 유해위험요인 건수 추이',
        left: 'left', // 왼쪽 정렬
        //top: 'top',   // 필요시 위치 조정 가능
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 13,
          color: '#ffffff',
          //textAlign: 'left' // 필요하면 추가
        }
      },
      tooltip: { // 툴팁 설정
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 13,
          color: '#ffffff',
        },
        backgroundColor: alpha('#111111', 0.9),
        borderColor: alpha('#ffffff', 0.25),
        borderWidth: 1,
      },
      legend: { // 범례 설정
        data: ['유해위험요인 건수', '고위험 건수'],
        bottom: 0,
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.REGULAR,
          fontSize: 12,
          color: '#ffffff',
        },
      },
      grid: { // 그리드 설정
        top: '18%',
        left: '4%',
        right: '4%',
        bottom: '10%',
        containLabel: true,
      },
      xAxis: { // x축 설정
        type: 'category',
        boundaryGap: false,
        data: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
        axisLabel: { // x축 레이블 설정
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.REGULAR, // x축 폰트 굵기
          fontSize: 12,
          color: '#ffffff',
        },
      },
      yAxis: { // y축 설정
        type: 'value',
        axisLabel: {
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.REGULAR, // x축 폰트 굵기
          fontSize: 12,
          color: '#ffffff',
        },
      },
      series: [ // 그래프 설정
        {
          name: '유해위험요인 건수',
          type: 'line',
          //stack: 'Total', // 그래프 스택으로 쌓을 지 여부
          symbol: 'none', // 포인트 제거
          areaStyle: {
            opacity: 0.2, // 연한 영역 색
          },
          itemStyle: {
            color: '#B3CDE3',
          },
          data: chartData.monthly_register_cause_count,
          //data: [50, 120, 150, 160, 180, 220, 280, 310, 320, 330, 340, 360],
        },
        {
          name: '고위험 건수',
          type: 'line',
          //stack: 'Total', // 그래프 스택으로 쌓을 지 여부
          symbol: 'none', // 포인트 제거
          areaStyle: {
            opacity: 0.2, // 연한 영역 색
          },
          itemStyle: {
            color: '#FBB4AE',
          },
          data: chartData.monthly_high_risk_count,
          //data: [10, 40, 80, 100, 120, 150, 160, 170, 180, 185, 190, 200],
        },
      ],
    };

    myChart.setOption(option);

    // resize 대응
    window.addEventListener('resize', () => myChart.resize());

    return () => {
      myChart.dispose();
    };
  }, [chartData]);

  return (
    <Card
      sx={{ 
        width: '100%',
        height: '100%',
        //mx: 'auto',
        border: `2px solid ${alpha("#111111", 0.20)}`, 
        borderRadius: "0.375rem", 
        bgcolor: alpha("#111111", 0.5),
        //overflow: 'hidden'
      }}
    >
      <CardContent sx={{ mt: 1, p: 0, '&:last-child': { pb: 0 } }}>
        <div ref={chartRef} style={{ height: 255, width: '100%' }} />
      </CardContent>
    </Card>
  );
};
// 유해위험요인 건수 3년 추이 차트 (부분 도넛 차트)
const ThreeYearTrendRegisterCauseChart = ({ chartData }) => {
  //const theme = useTheme();
  const chartRef = useRef(null);

  useEffect(() => {
    if(!chartData || chartData.length === 0) {
      return;
    }

    const chart = echarts.init(chartRef.current);
    const dummyValue = (chartData.high_risk_count + chartData.row_risk_count) * 0.25; // 빈공간을 위한 더미 값

    // 고위험 건수와 위험 건수를 각각의 데이터로 설정
    const realData = [
      { 
        value: chartData.high_risk_count,
        name: '고위험 건수',
        itemStyle: 
        { 
          color: '#FDCDAC',
          borderWidth: 2,
          borderColor: '#fff',
          borderRadius: 4,
        }
      },
      { 
        value: chartData.row_risk_count,
        name: '위험 건수',
        itemStyle:
        { 
          color: '#B3E2CD',
          borderWidth: 2,
          borderColor: '#fff',
          borderRadius: 4,
        } 
      },
    ];
    // 빈공간을 위한 더미 데이터
    const dummyData = {
      value: dummyValue,
      name: '빈공간',
      itemStyle: 
      { 
        color: 'transparent'
      }, 
      tooltip: { show: false }, label: { show: false } 
    };
  
    // 총 건수 계산
    const total = realData.reduce((sum, d) => sum + d.value, 0);
    // 비율 계산
    //const highRiskPercent = ((chartData.high_risk_count / total) * 100).toFixed(1);
    //const normalRiskPercent = ((chartData.row_risk_count / total) * 100).toFixed(1);

    // 차트 옵션 설정
    const option = {
      title: {
        text: total,
        left: 'center',
        top: '45%',
        textStyle: {
          fontSize: 24,
          fontWeight: FONT_PROPS.SEMIBOLD,
          align: 'center',
          color: '#ffffff',
        }
      },
      tooltip: {
        trigger: 'item',
        axisPointer: { type: 'shadow' },
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 13,
          color: '#ffffff',
        },
        backgroundColor: alpha('#111111', 0.9),
        borderColor: alpha('#ffffff', 0.25),
        borderWidth: 1,
        appendToBody: true,   // 부모 overflow 영향 제거 (ECharts 5.4+)
        confine: false,       // 기본값. 필요하면 true로 바꿔 컨테이너 안에 가두기
        //formatter: '{b}: {c}건 ({d}%)'
        formatter: function (params) {
          // 빈공간 제외
          const found = realData.find(d => d.name === params.name);
          if (!found) return ''; // 빈공간이면 표시 안함
          const percent = ((params.value / total) * 100).toFixed(1);
          return `
            <div>
              <strong>${params.name}</strong>
              <br/>건수: ${params.value}<br/>
              비율: ${percent}%
            </div>
          `;
        },
      },
      series: [
        {
          name: '건수',
          type: 'pie',
          radius: ['60%', '85%'], // 도넛 크기 (안쪽, 바깥쪽)
          center: ['50%', '50%'], // 차트 중앙
          startAngle: 235, // 어디부터 시작할지 조절
          //label: { show: false },
          label: {
            show: true,
            position: 'inner', // 또는 'center', 'inner', 'outside'
            formatter: function (params) {
              return params.value === 0 ? '' : params.value;
            },
            //formatter: '{c}',   // '{c}' = value, '{b}' = name, '{d}%' = percent
            fontSize: 12,
            fontWeight: FONT_PROPS.MEDIUM,
            fontFamily: FONT_PROPS.PRIMARY,
            color: '#111111'
          },
          labelLine: { show: false },
          // data: [
          //   { 
          //     value: highRisk,
          //     name: '고위험 건수',
          //     itemStyle: 
          //     { 
          //       color: '#FDCDAC',
          //       borderWidth: 2,
          //       borderColor: '#fff',
          //       borderRadius: 4,
          //     }
          //   },
          //   { 
          //     value: normalRisk,
          //     name: '위험 건수',
          //     itemStyle:
          //     { 
          //       color: '#B3E2CD',
          //       borderWidth: 2,
          //       borderColor: '#fff',
          //       borderRadius: 4,
          //     } 
          //   },
          //   { 
          //     value: dummyValue,
          //     name: '빈공간',
          //     itemStyle: 
          //     { 
          //       color: 'transparent'
          //     }, 
          //     tooltip: { show: false }, label: { show: false } 
          //   }
          // ]
          data: [ ...realData, dummyData ],
        }
      ]
    };

    chart.setOption(option);
    return () => chart.dispose();
  }, [chartData]);

  return(
    <Box sx={{ textAlign: 'center' }}>
      {/* 도넛차트 */}
      <div ref={chartRef} style={{ display: 'inline-block', width: 80, height: 80 }} />
      <Box sx={{ transform: 'translateY(-20px)'}}>
        {/* 연도 */}
        <Typography variant="subtitle1" sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 13, color: '#ffffff' }}>
          {chartData?.year}년
        </Typography>
        {/* 위험 건수 */}
        <Box display="flex" justifyContent="center">
          <Box display="flex" alignItems="center" mr={2} >
            <Box sx={{ width: 10, height: 10, bgcolor: '#fbc4ab', borderRadius: '50%', mr: 1.5 }} />
            <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 12, color: '#ffffff' }}>고위험 건수</Typography>
          </Box>
          <Box display="flex" alignItems="center" >
            <Box sx={{ width: 10, height: 10, bgcolor: '#b9e4c9', borderRadius: '50%', mr: 1.5 }} />
            <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 12, color: '#ffffff' }}>위험 건수</Typography>
          </Box>
        </Box>
        {/* 위험 건수 퍼센테이지 */}
        <Box display="flex" justifyContent="center" mt={0.5} gap={10} sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 12, color: '#ffffff' }}>
          <Typography mr={2} sx={{fontWeight: 'inherit', fontSize: 'inherit', color: 'inherit'}} >{chartData?.high_risk_rate}%</Typography>
          <Typography sx={{fontWeight: 'inherit', fontSize: 'inherit', color: 'inherit'}}>{chartData?.row_risk_rate}%</Typography>
        </Box>
      </Box>
      {/* 총 건수 */}
      <Typography 
        sx={{
          display: 'inline-block',     // ✅ 줄에 맞춰 크기 조절
          width: 'fit-content',        // ✅ 글자 너비만큼 자동 조절
          height: 'fit-content',       // ✅ 높이도 텍스트에 맞게
          transform: 'translateY(-125px)',
          //fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 12,
          color: '#ffffff',
        }}
      >
        총 건수
      </Typography>
    </Box>
  ); 
};
// 월별 평가완료 지표
const MonthlyEvaluationLevelGrid = ({ cardData }) => {
  const monthData = [];
  for(let i = 0; i < 12; ++i) {
    monthData.push({ 
      month: i + 1, 
      count: cardData?.monthly_register_cause_count[i],
      alert: cardData?.monthly_uncompleted_count[i],
      alert_info: cardData?.monthly_uncompleted_info[i]
    });
  }
  // const monthData = [
  //   { month: 1, count: 12, alert: 3 },
  //   { month: 2, count: 12, alert: 0 },
  //   { month: 3, count: 12, alert: 3 },
  //   { month: 4, count: 12, alert: 3 },
  //   { month: 5, count: 12, alert: 3 },
  //   { month: 6, count: 12, alert: 3 },
  //   { month: 7, count: 12, alert: 3 },
  //   { month: 8, count: 12, alert: 0 },
  //   { month: 9, count: 12, alert: 3 },
  //   { month: 10, count: 12, alert: 0 },
  //   { month: 11, count: 12, alert: 3 },
  //   { month: 12, count: 12, alert: 3 },
  // ];

  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%',
        border: `2px solid ${alpha("#111111", 0.20)}`, 
        borderRadius: "0.375rem", 
        bgcolor: alpha("#111111", 0.5),
        //flex: 1,           // 남은 높이 전부 차지
        minHeight: 0,      // 중요: 자식이 높이를 넘길 수 있게
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 13, mt: 1, ml: 1, color: '#ffffff' }}>
        월별 위험성 평가 지표
      </Typography>
      <Grid 
        container 
        spacing={0.5} 
        p={1} 
        sx={{
          flex: 1,         // 남은 영역 전부
          minHeight: 0,    // 중요: 오버플로우 방지
          alignContent: 'stretch',
          alignItems: 'stretch',
          //overflow: 'auto' // 필요시 내부 스크롤
        }}
      >
        {monthData.map((item) => (
          <Grid 
            item 
            xs={12} sm={6} md={4} 
            key={item.month}
            sx={{ display: 'flex' }}   // 아이템 자체도 스트레치
          >
            <EvaluationCardTemplate {...item} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
// 위험성 평가완료 카드
const EvaluationCardTemplate = ({ month, count, alert, alert_info }) => {
  const theme = useTheme();
  const [detailModal, setDetailModal] = useState(false);
  const detailModalOpen = () => setDetailModal({ isOpen: true });
  const detailModalClose = () => setDetailModal({ isOpen: false });

  const isComplete = alert === 0;
  const isPending = alert === -1;
  const isIncomplete = !isComplete && !isPending;

  // 테이블 참조
  // const tableRef = useRef();
  // const clearSelection = () => { tableRef.current?.clearSelection(); };
  // const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  // const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  // const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  // const customTableTopToolbar = (table) => {
  //   return(
  //   <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
  //     {/* 왼쪽 영역 */}
  //     <Box display="flex" alignItems="center" gap={2}>
  //       {/* TODO: */}
  //     </Box>

  //     {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
  //     <Box display="flex" alignItems="center" gap={2}>
  //       {/* 검색창 */}
  //       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
  //         <IconButton onClick={() => setShowSearch((prev) => !prev)}>
  //           <SearchIcon />
  //         </IconButton>
        
  //         <Collapse in={showSearch} orientation="horizontal">
  //         <TextField
  //           size="small"
  //           placeholder="검색"
  //           value={globalFilter}
  //           onChange={(e) => {
  //             setGlobalFilter(e.target.value);
  //             table.setGlobalFilter(e.target.value);
  //           }}
  //           sx={{ minWidth: 200 }}
  //           variant="outlined"
  //           slots={{
  //             input: OutlinedInput,
  //           }}
  //           slotProps={{
  //             input: {
  //               endAdornment: (
  //                 <InputAdornment position="end">
  //                   <IconButton
  //                     onClick={() => {
  //                       setGlobalFilter('');
  //                       table.setGlobalFilter('');
  //                       setShowSearch(false); // 닫기
  //                     }}
  //                   >
  //                     <CloseIcon fontSize="small" />
  //                   </IconButton>
  //                 </InputAdornment>
  //               ),
  //             }
  //           }}
  //         />
  //         </Collapse>
  //       </Box>
  //     </Box>
  //   </Box>
  //   );
  // };

  return (
    <>
      <Card 
        sx={{
          width: '100%',
          height: '100%',
          border: `2px solid ${alpha("#111111", 0.20)}`, 
          borderRadius: "0.375rem", 
          bgcolor: alpha("#111111", 0.5),
        }}
      >
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {/* 매월 */}
          <Typography sx={{ fontWeight:FONT_PROPS.MEDIUM, fontSize: 12, color: '#ffffff' , mt: 1, ml: 1 }}>
            {month}월
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {/* 매월 유해위험요인 등록 수 */}
            <Typography sx={{ fontWeight:FONT_PROPS.MEDIUM, fontSize: 20, color: '#ffffff', mt: 0.5, ml: 2 }}>
              {count}
            </Typography>
            {/* 평가표시 */}
            <Box mt={1} display="flex" justifyContent="center" alignItems="center" gap={1}>
              {isComplete && (
                <>
                  <CheckCircleOutlineIcon sx={{ fontSize: 12, color: 'green' }} />
                  <Typography fontSize={12} sx={{ color: 'green' }}>
                    평가 완료
                  </Typography>
                </>
              )}
              {isPending && (
                <>
                  <RemoveIcon sx={{ fontSize: 12, color: '#C8C8DA' }} />
                  <Typography fontSize={12} sx={{ color: '#C8C8DA' }}>
                    평가 대기
                  </Typography>
                </>
              )}
              {isIncomplete && (
                <>
                  {/* <NotificationsActiveIcon sx={{ fontSize: 12, color: 'red' }} /> */}
                  <Typography fontSize={12} sx={{ color: 'red' }}>
                    평가 미완료
                  </Typography>
                </>
              )}
            </Box>
            {isIncomplete && (
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Tooltip title="상세보기" arrow placement="bottom">
                  <Typography 
                    fontSize={12} 
                    mt={0.5} 
                    sx={{ 
                      color: 'red',
                      display: 'inline-block',
                      px: 1,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha('#767676', 0.5),
                        // boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                      },
                    }}
                    onClick={detailModalOpen}
                  >
                    🔔 {alert}
                  </Typography>
                </Tooltip>
              </Box>
            )}
            
            {isPending && (
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography 
                  fontSize={12} 
                  mt={0.5} 
                  sx={{ 
                    color: 'red',
                    // display: 'inline-block',
                    // paddingX: 1,
                    // paddingY: 1,
                    // borderRadius: 1,
                    // cursor: 'pointer',
                    // transition: 'all 0.2s ease',
                    // '&:hover': {
                    //   backgroundColor: theme.palette.action.hover,
                    //   boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                    // },
                  }}
                  // onClick={handleUseModal}
                >
                  {/* 🔔 {alert} */}
                </Typography>
              </Box>
            )}
            {isComplete && (
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography 
                  fontSize={12}
                  mt={0.5}
                  sx={{
                    color: 'green',
                    // display: 'inline-block',
                    // paddingX: 1,
                    // paddingY: 1,
                    // borderRadius: 1,
                    // cursor: 'pointer',
                    // transition: 'all 0.2s ease',
                    // '&:hover': {
                    //   backgroundColor: theme.palette.action.hover,
                    //   boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                    // },
                  }}
                  //onClick={handleUseModal}
                >
                  {/* 🔔 {alert} */}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* 평가 미완료 상세정보 모달 */}
      <EvaluationUncompletedDetailsModal 
        isOpen={detailModal.isOpen} 
        onClose={detailModalClose}
        title={`${month}월 위험성 평가 미완료 상세정보`}
        data={(alert_info && alert_info.data) || []}
      />
      {/* <ModalTemplate 
        isOpen={evalModal}
        handleConfirm={handleEvalModal}
        handleCancel={handleEvalModal}
        confirmText="확인"
        enableSingleActionButton={true}
        title={`${month}월 위험성 평가 미완료 상세정보`}
        containerSX={{ width: 'auto', height: 'auto' }}
      >
        <NewTableTemplate
          columns={monitoringResultMonthlyInfoColumns}
          data={(alert_info && alert_info.data) || []}
          topToolbarContent={customTableTopToolbar}
          enableCustomGlobalFilter={true}
          enabledSelectionRow={false}
          enabledSelectionAllRow={false}
          templateSX ={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem", 
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
          }}
        />
      </ModalTemplate > */}
    </>
  );
};
// 위험성 평가율 3년 추이 차트
const EvaluationRateDonut = ({ chartData }) => {
  const chartRef = useRef(null);
  const remaining = 100 - (chartData?.eval_percent || 0);

  useEffect(() => {
    const chart = echarts.init(chartRef.current);

    const option = {
      tooltip: { show: false },
      legend: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['70%', '90%'],
          center: ['50%', '50%'],
          startAngle: 90,
          label: {
            show: true,
            position: 'center',
            formatter: `${chartData?.eval_percent || 0}%`,
            fontSize: 20,
            fontWeight: FONT_PROPS.SEMIBOLD,
            //color: '#333'
          },
          labelLine: { show: false },
          data: [
            {
              value: chartData?.eval_percent || 0,
              name: '평가율',
              itemStyle: {
                color: '#6287DB'
              }
            },
            {
              value: remaining,
              name: '나머지',
              itemStyle: {
                color: '#dee6f6'
              },
              tooltip: { show: false }
            }
          ]
        }
      ]
    };

    chart.setOption(option);
    return () => chart.dispose();
  }, [chartData]);

  return (
    <Box textAlign="center">
      <div ref={chartRef} style={{ display: 'inline-block', width: 120, height: 120 }} />
      <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 13, color: '#fff' }}>
        {chartData?.year || 0}년
      </Typography>
    </Box>
  );
};

const monthlyEvaluationColumnDefs = [
  {
    field: "process_name",
    headerName: "공정명",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
  },
  {
    field: "process_description",
    headerName: "공정내용",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
  },
  {
    field: "danger_category",
    headerName: "위험분류",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
  },
  {
    field: "danger_cause",
    headerName: "위험원인",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    // valueFormatter: (params) => {
    //   const level = params.value;
    //   switch (level) {
    //     case 1: return "하";
    //     case 2: return "중";
    //     case 3: return "상";
    //     default: return "-";
    //   }
    // }
  },
  {
    field: "danger_description",
    headerName: "위험요인 발생상황 및 결과",
    flex: 1,
    minWidth: 250,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    // valueFormatter: (params) => {
    //   return params.value ? params.value : '-';
    // }
    //sort: 'asc',
  },
  {
    field: "machine_name",
    headerName: "대상 기계/기구",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      const values = params.data;
      return values.safe_machine_name || values.machine_name;
    }
  },
  {
    field: "machine_manage_code",
    headerName: "관리번호",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    // valueFormatter: (params) => {
    //   return params.value ? params.value : '-';
    // }
  },
  {
    field: "delete_date",
    headerName: "삭제일자",
    flex: 1,
    minWidth: 120,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      const values = params.data;
      let date = '-';
      if(values.process_delete_date) {
        date = dayjs(values.process_delete_date).format('YYYY-MM-DD');
      }
      else if(values.register_asset_delete_date) {
        date = dayjs(values.register_asset_delete_date).format('YYYY-MM-DD');
      }
      else if(values.register_cause_delete_date) {
        date = dayjs(values.register_cause_delete_date).format('YYYY-MM-DD');
      }
      return date;
    }
  },
];
// 월별 위험성 평가 미완료 상세정보 모달 컴포넌트
const EvaluationUncompletedDetailsModal = ({
  isOpen = false,
  onClose = null,
  title="",
  data,
}) => {

  const gridOptions = {
    pagination: true,
    paginationPageSize: 20,
    rowHeight: 52,
    headerHeight: 52,
    suppressCellFocus: true,
    defaultColDef: {
      sortable: true,
      filter: false,
      resizable: true,
    },
    suppressMenuHide: true,
    suppressContextMenu: true,
    enableCellTextSelection: false,
  };

  return(
    <Dialog
      fullWidth
      open={isOpen}
      maxWidth="lg"
      scroll="body"
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") { onClose(); }
      }}
      sx={{
        "& .MuiDialog-paper": {
          overflow: "visible",
          backgroundColor: "rgba(47, 51, 73, 0.8);",
          border: "solid 2px rgb(63 68 99)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            backgroundColor: "rgba(0,0,0,0.3)",
          },
        },
      }}
    >
      <Box sx={{ borderBottom: "solid 1px #464646;", padding: "16px 24px", }}>
        <Typography variant="h3" sx={{ color: "#ced4da" }}>
          {title}
        </Typography>
      </Box>
      <DialogContent
        sx={{
          px: (theme) => [
            `${theme.spacing(5)} !important`,
            `${theme.spacing(10)} !important`,
          ],
          py: (theme) => [
            `${theme.spacing(8)} !important`,
            `${theme.spacing(10)} !important`,
          ],
        }}
      >
        <div style={{ height: '500px', width: '100%' }}>
          <AgGridReact
            rowData={data}
            columnDefs={monthlyEvaluationColumnDefs}
            gridOptions={gridOptions}
            theme={myTheme}
            //suppressFiltersToolPanel={true}
            //suppressColumnFilter={true}
            //suppressRowClickSelection={true}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary"onClick={onClose} >
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/////////////////////////////////////////////////

// 개선 대책 결과 지표
const StrategyView = ({ group_code, project_code, referenceDate }) => {
  const theme = useTheme();

  // 지표 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getStrategyResultData",
    group_code,
    project_code,
    { params: { date: referenceDate } },
  );

  const [tempGetData, setTempGetData] = useState([]);
  // getData 변경될 때마다 상태 업데이트
  useEffect(() => {
    if(!getData.data || getData.data.length === 0) {
      return;
    }
    const tempData = { ...getData.data };
    setTempGetData(tempData);
  }, [getData.data]);

  // 선택한 평가연도의 개선대책수립 지표 데이터 가져오기
  const getSelectedYearEstablishmentData = (year) => {
    const data = tempGetData.strategyEstablishmentData?.find(item => item.year === year);
    return data;
  };
  // 선택한 평가연도의 개선대책실행 지표 데이터 가져오기
  const getSelectedYearExecutionData = (year) => {
    const data = tempGetData.strategyExecutionData?.find(item => item.year === year);
    return data;
  };

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', gap: 2, p: 1 }}>
      {/** 월별 개선대책수립 건수 추이 */}
      <Box sx={{ width: '100%', height: '100%' }}>
        <MonthlyTrendStrategyChart 
          chartData={getSelectedYearEstablishmentData(Number(dayjs(referenceDate).format('YYYY')))} 
        />
      </Box>
      {/** 개선대책수립 건수 3년 추이 */}
      <Box sx={{ width: '100%', height: '100%' }}>
        <Card
          sx={{ 
            width: '100%',
            height: '100%',
            border: `2px solid ${alpha("#111111", 0.20)}`,
            borderRadius: "0.375rem", 
            bgcolor: alpha("#111111", 0.5),
          }}
        >
          <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 13, mt: 1, ml: 1, mb: 10, color: '#ffffff' }}>
            개선대책수립 건수 3년 추이
          </Typography>
          <Box display="flex" justifyContent="space-around" alignItems="center" flex={1}>
            <ThreeYearTrendStrategyChart 
              chartData={getSelectedYearEstablishmentData(Number(dayjs(referenceDate).format('YYYY')) - 2)} 
            />
            <ThreeYearTrendStrategyChart 
              chartData={getSelectedYearEstablishmentData(Number(dayjs(referenceDate).format('YYYY')) - 1)}
            />
            <ThreeYearTrendStrategyChart 
              chartData={getSelectedYearEstablishmentData(Number(dayjs(referenceDate).format('YYYY')))} 
            />
          </Box>
          {/* 범례 */}
          <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
            <Box sx={{ width: 10, height: 10, bgcolor: '#b9e4c9', borderRadius: '50%' }} />
            <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 12, color: '#ffffff' }}>
              개선대책수립 총 건수
            </Typography>
          </Box>
        </Card>
      </Box>
      {/** 월별 개선대책 실행 지표 */}
      <Box sx={{ width: '100%', height: '100%' }}>
        <MonthlyStrategyExecutionGrid 
          cardData={getSelectedYearExecutionData(Number(dayjs(referenceDate).format('YYYY')))} 
        />
      </Box>
      {/** 개선대책 실행율 3년 추이 */}
      <Box sx={{ width: '100%', height: '100%' }}>
        <Card sx={{ width: '100%',height: '100%',border: `2px solid ${alpha("#111111", 0.20)}`,borderRadius: "0.375rem", bgcolor: alpha("#111111", 0.5),}}>
          <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 13, color: '#ffffff', mt: 1, ml: 1, mb: 10 }}>
            개선대책 실행율 3년 추이
          </Typography>
          <Box display="flex" justifyContent="space-around" alignItems="center" flex={1}>
            <StrategyExecutionRateDonut 
              chartData={getSelectedYearExecutionData(Number(dayjs(referenceDate).format('YYYY')) - 2)} 
            />
            <StrategyExecutionRateDonut 
              chartData={getSelectedYearExecutionData(Number(dayjs(referenceDate).format('YYYY')) - 1)} 
            />
            <StrategyExecutionRateDonut 
              chartData={getSelectedYearExecutionData(Number(dayjs(referenceDate).format('YYYY')))} 
            />
          </Box>
          <Box mt={6} display="flex" justifyContent="center" alignItems="center" gap={2} >
            <Box width={10} height={10} borderRadius="50%" bgcolor="#6287DB" />
            <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 12, color: '#fff' }}>
              실행율
            </Typography>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};
// 월별 개선대책수립 건수 추이 차트
const MonthlyTrendStrategyChart = ({ chartData }) => {
  const theme = useTheme();
  const chartRef = useRef(null);

  useEffect(() => {
    if(!chartData || chartData.length === 0) {
      return;
    }

    const myChart = echarts.init(chartRef.current);

    const option = {
      title: { // 타이틀 설정
        text: '월별 개선대책수립 건수 추이',
        left: 'left', // 왼쪽 정렬
        //top: 'top',   // 필요시 위치 조정 가능
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 13,
          color: '#ffffff',
          //textAlign: 'left' // 필요하면 추가
        }
      },
      tooltip: { // 툴팁 설정
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 13,
          color: '#ffffff',
        },
        backgroundColor: alpha('#111111', 0.9),
        borderColor: alpha('#ffffff', 0.25),
        borderWidth: 1,
      },
      legend: { // 범례 설정
        data: ['개선대책수립 총 건수'],
        bottom: 0,
        textStyle: {
          fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.REGULAR,
          fontSize: 12,
          color: '#ffffff',
        },
      },
      grid: { // 그리드 설정
        top: '18%',
        left: '4%',
        right: '4%',
        bottom: '10%',
        containLabel: true,
      },
      xAxis: { // x축 설정
        type: 'category',
        boundaryGap: false,
        data: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
        axisLabel: { // x축 레이블 설정
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.REGULAR, // x축 폰트 굵기
          fontSize: 12,
          color: '#ffffff',
        },
      },
      yAxis: { // y축 설정
        type: 'value',
        axisLabel: {
          fontFamily: FONT_PROPS.PRIMARY, // x축 폰트 패밀리
          fontWeight: FONT_PROPS.REGULAR, // x축 폰트 굵기
          fontSize: 12,
          color: '#ffffff',
        },
      },
      series: [ // 그래프 설정
        {
          name: '개선대책수립 총 건수',
          type: 'line',
          //stack: 'Total', // 그래프 스택으로 쌓을 지 여부
          symbol: 'none', // 포인트 제거
          areaStyle: {
            opacity: 0.2, // 연한 영역 색
          },
          itemStyle: {
            color: '#B3CDE3',
          },
          data: chartData.monthly_establishment_count,
          //data: [50, 120, 150, 160, 180, 220, 280, 310, 320, 330, 340, 360],
        },
        // {
        //   name: '고위험 건수',
        //   type: 'line',
        //   //stack: 'Total', // 그래프 스택으로 쌓을 지 여부
        //   symbol: 'none', // 포인트 제거
        //   areaStyle: {
        //     opacity: 0.2, // 연한 영역 색
        //   },
        //   itemStyle: {
        //     color: '#FBB4AE',
        //   },
        //   data: chartData.monthly_high_risk_count,
        //   //data: [10, 40, 80, 100, 120, 150, 160, 170, 180, 185, 190, 200],
        // },
      ],
    };

    myChart.setOption(option);

    // resize 대응
    window.addEventListener('resize', () => myChart.resize());

    return () => {
      myChart.dispose();
    };
  }, [chartData]);

  return (
    <Card
      sx={{ 
        width: '100%',
        height: '100%',
        //mx: 'auto',
        border: `2px solid ${alpha("#111111", 0.20)}`, 
        borderRadius: "0.375rem", 
        bgcolor: alpha("#111111", 0.5),
        //overflow: 'hidden'
      }}
    >
      <CardContent sx={{ mt: 1, p: 0, '&:last-child': { pb: 0 } }}>
        <div ref={chartRef} style={{ height: 255, width: '100%' }} />
      </CardContent>
    </Card>
  );
};
// 개선대책수립 건수 3년 추이 차트 (부분 도넛 차트)
const ThreeYearTrendStrategyChart = ({ chartData }) => {
  //const theme = useTheme();
  const chartRef = useRef(null);

  useEffect(() => {
    if(!chartData || chartData.length === 0) {
      return;
    }

    const chart = echarts.init(chartRef.current);
    const dummyValue = chartData.establishment_total_count * 0.25; // 빈공간을 위한 더미 값

    // 고위험 건수와 위험 건수를 각각의 데이터로 설정
    const realData = [
      { 
        value: chartData.establishment_total_count,
        name: '개선대책수립 총 건수',
        itemStyle:
        { 
          color: '#B3E2CD',
          borderWidth: 2,
          borderColor: '#fff',
          borderRadius: 4,
        } 
      },
    ];
    // 빈공간을 위한 더미 데이터
    const dummyData = {
      value: dummyValue,
      name: '빈공간',
      itemStyle: 
      { 
        color: 'transparent'
      }, 
      tooltip: { show: false }, label: { show: false } 
    };
  
    // 총 건수 계산
    const total = realData.reduce((sum, d) => sum + d.value, 0);

    // 차트 옵션 설정
    const option = {
      title: {
        text: total,
        left: 'center',
        top: '45%',
        textStyle: {
          fontSize: 24,
          fontWeight: FONT_PROPS.SEMIBOLD,
          align: 'center',
          color: '#ffffff',
        }
      },
      series: [
        {
          name: '건수',
          type: 'pie',
          radius: ['60%', '85%'], // 도넛 크기 (안쪽, 바깥쪽)
          center: ['50%', '50%'], // 차트 중앙
          startAngle: 235, // 어디부터 시작할지 조절
          label: { show: false },
          labelLine: { show: false },
          data: [ ...realData, dummyData ],
        }
      ]
    };

    chart.setOption(option);
    return () => chart.dispose();
  }, [chartData]);

  return(
    <Box sx={{ textAlign: 'center' }}>
      {/* 도넛차트 */}
      <div ref={chartRef} style={{ display: 'inline-block', width: 120, height: 120 }} />
      <Box sx={{ transform: 'translateY(-30px)'}}>
        {/* 연도 */}
        <Typography variant="subtitle1" sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 13, color: '#ffffff' }}>
          {chartData?.year}년
        </Typography>
      </Box>
      {/* 총 건수 */}
      <Typography 
        sx={{
          display: 'inline-block',     // ✅ 줄에 맞춰 크기 조절
          width: 'fit-content',        // ✅ 글자 너비만큼 자동 조절
          height: 'fit-content',       // ✅ 높이도 텍스트에 맞게
          transform: 'translateY(-115px)',
          // fontFamily: FONT_PROPS.PRIMARY,
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 14,
          color: '#ffffff',
        }}>
        총 건수
      </Typography>
    </Box>
  ); 
};
// 월별 개선대책 실행 지표
const MonthlyStrategyExecutionGrid = ({ cardData }) => {
  const monthData = [];
  for(let i = 0; i < 12; ++i) {
    monthData.push({ 
      month: i + 1, 
      count: cardData?.monthly_execution_count[i],
      alert: cardData?.monthly_uncompleted_info[i].count,
      alert_info: cardData?.monthly_uncompleted_info[i]
    });
  }
  // const monthData = [
  //   { month: 1, count: 12, alert: 3 },
  //   { month: 2, count: 12, alert: 0 },
  //   { month: 3, count: 12, alert: 3 },
  //   { month: 4, count: 12, alert: 3 },
  //   { month: 5, count: 12, alert: 3 },
  //   { month: 6, count: 12, alert: 3 },
  //   { month: 7, count: 12, alert: 3 },
  //   { month: 8, count: 12, alert: 0 },
  //   { month: 9, count: 12, alert: 3 },
  //   { month: 10, count: 12, alert: 0 },
  //   { month: 11, count: 12, alert: 3 },
  //   { month: 12, count: 12, alert: 3 },
  // ];

  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%',
        border: `2px solid ${alpha("#111111", 0.20)}`, 
        borderRadius: "0.375rem", 
        bgcolor: alpha("#111111", 0.5),
        //flex: 1,           // 남은 높이 전부 차지
        minHeight: 0,      // 중요: 자식이 높이를 넘길 수 있게
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 13, mt: 1, ml: 1, color: '#ffffff' }}>
        월별 개선대책 실행 지표
      </Typography>
      <Grid 
        container 
        spacing={0.5} 
        p={1} 
        sx={{
          flex: 1,         // 남은 영역 전부
          minHeight: 0,    // 중요: 오버플로우 방지
          alignContent: 'stretch',
          alignItems: 'stretch',
          //overflow: 'auto' // 필요시 내부 스크롤
        }}
      >
        {monthData.map((item) => (
          <Grid 
            item 
            xs={12} sm={6} md={4} 
            key={item.month}
            sx={{ display: 'flex' }}   // 아이템 자체도 스트레치
          >
            <StrategyExecutionCardTemplate {...item} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
// 개선대책 실행 카드
const StrategyExecutionCardTemplate = ({ month, count, alert, alert_info }) => {
  const theme = useTheme();
  const [detailModal, setDetailModal] = useState(false);
  const detailModalOpen = () => setDetailModal({ isOpen: true });
  const detailModalClose = () => setDetailModal({ isOpen: false });

  const isComplete = alert === 0;
  const isPending = alert === -1;
  const isIncomplete = !isComplete && !isPending;

  // 테이블 참조
  // const tableRef = useRef();
  // const handleSelectedReset = () => { tableRef.current?.clearSelection(); };
  // const handleGetSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  // const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  // const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  // const customTableTopToolbar = (table) => {
  //   return(
  //   <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
  //     {/* 왼쪽 영역 */}
  //     <Box display="flex" alignItems="center" gap={2}>
  //       {/* TODO: */}
  //     </Box>

  //     {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
  //     <Box display="flex" alignItems="center" gap={2}>
  //       {/* 검색창 */}
  //       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
  //         <IconButton onClick={() => setShowSearch((prev) => !prev)}>
  //           <SearchIcon />
  //         </IconButton>
        
  //         <Collapse in={showSearch} orientation="horizontal">
  //         <TextField
  //           size="small"
  //           placeholder="검색"
  //           value={globalFilter}
  //           onChange={(e) => {
  //             setGlobalFilter(e.target.value);
  //             table.setGlobalFilter(e.target.value);
  //           }}
  //           sx={{ minWidth: 200 }}
  //           variant="outlined"
  //           slots={{
  //             input: OutlinedInput,
  //           }}
  //           slotProps={{
  //             input: {
  //               endAdornment: (
  //                 <InputAdornment position="end">
  //                   <IconButton
  //                     onClick={() => {
  //                       setGlobalFilter('');
  //                       table.setGlobalFilter('');
  //                       setShowSearch(false); // 닫기
  //                     }}
  //                   >
  //                     <CloseIcon fontSize="small" />
  //                   </IconButton>
  //                 </InputAdornment>
  //               ),
  //             }
  //           }}
  //         />
  //         </Collapse>
  //       </Box>
  //     </Box>
  //   </Box>
  //   );
  // };

  return (
    <>
      <Card 
        sx={{
          width: '100%',
          height: '100%',
          border: `2px solid ${alpha("#111111", 0.20)}`, 
          borderRadius: "0.375rem", 
          bgcolor: alpha("#111111", 0.5),
        }}
      >
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {/* 매월 */}
          <Typography sx={{ fontWeight:FONT_PROPS.MEDIUM, fontSize: 12, color: '#ffffff' , mt: 1, ml: 1 }}>
            {month}월
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {/* 매월 유해위험요인 등록 수 */}
            <Typography sx={{ fontWeight:FONT_PROPS.MEDIUM, fontSize: 20, color: '#ffffff', mt: 0.5, ml: 2 }}>
              {count}
            </Typography>
            {/* 개선표시 */}
            <Box mt={1} display="flex" justifyContent="center" alignItems="center" gap={1}>
              {isComplete && (
                <>
                  <CheckCircleOutlineIcon sx={{ fontSize: 12, color: 'green' }} />
                  <Typography fontSize={12} sx={{ color: 'green' }}>
                    개선 완료
                  </Typography>
                </>
              )}
              {isPending && (
                <>
                  <RemoveIcon sx={{ fontSize: 12, color: '#C8C8DA' }} />
                  <Typography fontSize={12} sx={{ color: '#C8C8DA' }}>
                    개선 대기
                  </Typography>
                </>
              )}
              {isIncomplete && (
                <>
                  {/* <NotificationsActiveIcon fontSize="small" sx={{ color: 'red' }} /> */}
                  <Typography fontSize={12} sx={{ color: 'red' }}>
                    개선 미완료
                  </Typography>
                </>
              )}
            </Box>
            {isIncomplete && (
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Tooltip title="상세보기" arrow placement="bottom">
                  <Typography 
                    fontSize={12} 
                    mt={0.5} 
                    sx={{ 
                      color: 'red',
                      display: 'inline-block',
                      px: 1,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha('#767676', 0.5),
                        // boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                      },
                    }}
                    onClick={detailModalOpen}
                  >
                    🔔 {alert}
                  </Typography>
                </Tooltip>
              </Box>
            )}
            
            {isPending && (
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography 
                  fontSize={12} 
                  mt={0.5} 
                  sx={{ 
                    color: 'red',
                    // display: 'inline-block',
                    // paddingX: 1,
                    // paddingY: 1,
                    // borderRadius: 1,
                    // cursor: 'pointer',
                    // transition: 'all 0.2s ease',
                    // '&:hover': {
                    //   backgroundColor: theme.palette.action.hover,
                    //   boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                    // },
                  }}
                  // onClick={handleUseModal}
                >
                  {/* 🔔 {alert} */}
                </Typography>
              </Box>
            )}
            {isComplete && (
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography 
                  fontSize={12}
                  mt={0.5}
                  sx={{
                    color: 'green',
                    // display: 'inline-block',
                    // paddingX: 1,
                    // paddingY: 1,
                    // borderRadius: 1,
                    // cursor: 'pointer',
                    // transition: 'all 0.2s ease',
                    // '&:hover': {
                    //   backgroundColor: theme.palette.action.hover,
                    //   boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                    // },
                  }}
                  //onClick={handleUseModal}
                >
                  {/* 🔔 {alert} */}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* 개선 미완료 상세정보 모달 */}
      <StrategyUncompletedDetailsModal 
        isOpen={detailModal.isOpen} 
        onClose={detailModalClose}
        title={`${month}월 개선대책 미완료 상세정보`}
        data={(alert_info && alert_info.data) || []}
      />
      {/* <ModalTemplate 
        isOpen={strategyModal}
        handleConfirm={handleStrategyModal}
        handleCancel={handleStrategyModal}
        confirmText="확인"
        enableSingleActionButton={true}
        title={`${month}월 개선대책 미완료 상세정보`}
        containerSX={{ width: 'auto', height: 'auto' }}
      >
        <NewTableTemplate 
          columns={monitoringStrategyMonthlyInfoColumns}
          data={(alert_info && alert_info.data) || []}
          topToolbarContent={customTableTopToolbar}
          enableCustomGlobalFilter={true}
          enabledSelectionRow={false}
          enabledSelectionAllRow={false}
          templateSX ={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "0.375rem", 
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
          }}
        />
      </ModalTemplate > */}
    </>
  );
};
// 실행율 3년 추이 차트 (도넛 차트)
const StrategyExecutionRateDonut = ({ chartData }) => {
  const chartRef = useRef(null);
  const remaining = 100 - (chartData?.execution_percent || 0);

  useEffect(() => {
    const chart = echarts.init(chartRef.current);

    const option = {
      tooltip: { show: false },
      legend: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['70%', '90%'],
          center: ['50%', '50%'],
          startAngle: 90,
          label: {
            show: true,
            position: 'center',
            formatter: `${chartData?.execution_percent || 0}%`,
            fontSize: 20,
            fontWeight: FONT_PROPS.SEMIBOLD,
            //color: '#333'
          },
          labelLine: { show: false },
          data: [
            {
              value: chartData?.execution_percent || 0,
              name: '실행율',
              itemStyle: {
                color: '#6287DB'
              }
            },
            {
              value: remaining,
              name: '나머지',
              itemStyle: {
                color: '#dee6f6'
              },
              tooltip: { show: false }
            }
          ]
        }
      ]
    };

    chart.setOption(option);
    return () => chart.dispose();
  }, [chartData]);

  return (
    <Box textAlign="center">
      <div ref={chartRef} style={{ display: 'inline-block', width: 120, height: 120 }} />
      <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 13, color: '#fff' }}>
        {chartData?.year || 0}년
      </Typography>
    </Box>
  );
};

const monthlyStrategyColumnDefs = [
  {
    field: "process_name",
    headerName: "공정명",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
  },
  {
    field: "process_description",
    headerName: "공정내용",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
  },
  {
    field: "danger_category",
    headerName: "위험분류",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
  },
  {
    field: "danger_cause",
    headerName: "위험원인",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    // valueFormatter: (params) => {
    //   const level = params.value;
    //   switch (level) {
    //     case 1: return "하";
    //     case 2: return "중";
    //     case 3: return "상";
    //     default: return "-";
    //   }
    // }
  },
  {
    field: "danger_description",
    headerName: "위험요인 발생상황 및 결과",
    flex: 1,
    minWidth: 250,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    // valueFormatter: (params) => {
    //   return params.value ? params.value : '-';
    // }
    //sort: 'asc',
  },
  {
    field: "machine_name",
    headerName: "대상 기계/기구",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      const values = params.data;
      return values.safe_machine_name || values.machine_name;
    }
  },
  {
    field: "machine_manage_code",
    headerName: "관리번호",
    flex: 1,
    minWidth: 80,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    // valueFormatter: (params) => {
    //   return params.value ? params.value : '-';
    // }
  },
  {
    field: "current_risk_level",
    headerName: "현재 위험성",
    flex: 1,
    minWidth: 120,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      const values = params.data;
      const currentValue = riskLevelMapper[values.standard_id];
      const text = currentValue ? `${currentValue}(${values.frequency_level * values.importancy_level})` : "-";
      return text;
    }
  },
  {
    field: "create_date",
    headerName: "대책수립일자",
    flex: 1,
    minWidth: 120,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      const values = params.data;
      return values.expected_date ? dayjs(values.expected_date).format('YYYY-MM-DD') : '-';
    }
  },
  {
    field: "delete_date",
    headerName: "삭제일자",
    flex: 1,
    minWidth: 120,
    headerClass: "ag-header-center",
    cellStyle: { 
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    valueFormatter: (params) => {
      const values = params.data;
      let date = '-';
      if(values.process_delete_date) {
        date = dayjs(values.process_delete_date).format('YYYY-MM-DD');
      }
      else if(values.register_asset_delete_date) {
        date = dayjs(values.register_asset_delete_date).format('YYYY-MM-DD');
      }
      else if(values.register_cause_delete_date) {
        date = dayjs(values.register_cause_delete_date).format('YYYY-MM-DD');
      }
      return date;
    }
  },
];
// 월별 개선대책 미완료 상세정보 모달 컴포넌트
const StrategyUncompletedDetailsModal = ({
  isOpen = false,
  onClose = null,
  title="",
  data,
}) => {

  const gridOptions = {
    pagination: true,
    paginationPageSize: 20,
    rowHeight: 52,
    headerHeight: 52,
    suppressCellFocus: true,
    defaultColDef: {
      sortable: true,
      filter: false,
      resizable: true,
    },
    suppressMenuHide: true,
    suppressContextMenu: true,
    enableCellTextSelection: false,
  };

  return(
    <Dialog
      fullWidth
      open={isOpen}
      maxWidth="lg"
      scroll="body"
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") { onClose(); }
      }}
      sx={{
        "& .MuiDialog-paper": {
          overflow: "visible",
          backgroundColor: "rgba(47, 51, 73, 0.8);",
          border: "solid 2px rgb(63 68 99)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            backgroundColor: "rgba(0,0,0,0.3)",
          },
        },
      }}
    >
      <Box sx={{ borderBottom: "solid 1px #464646;", padding: "16px 24px", }}>
        <Typography variant="h3" sx={{ color: "#ced4da" }}>
          {title}
        </Typography>
      </Box>
      <DialogContent
        sx={{
          px: (theme) => [
            `${theme.spacing(5)} !important`,
            `${theme.spacing(10)} !important`,
          ],
          py: (theme) => [
            `${theme.spacing(8)} !important`,
            `${theme.spacing(10)} !important`,
          ],
        }}
      >
        <div style={{ height: '500px', width: '100%' }}>
          <AgGridReact
            rowData={data}
            columnDefs={monthlyStrategyColumnDefs}
            gridOptions={gridOptions}
            theme={myTheme}
            //suppressFiltersToolPanel={true}
            //suppressColumnFilter={true}
            //suppressRowClickSelection={true}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary"onClick={onClose} >
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};

////////////////////////////////////
// 토글 버튼 (드로어 밖, 고정 위치)
function DrawerToggleButton({
  isOpen,
  onToggle,
  leftWidth,
  rightWidth,
  ariaControlsId,
  drawerHeight = 0,
  drawerGap = 0,
}) {
  return (
    <Box
      sx={{
        position: 'fixed',
        left: leftWidth,
        right: rightWidth,
        bottom: isOpen ? drawerHeight + drawerGap : drawerGap,
        zIndex: 1201, // 드로어(paper 1200)보다 위
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none', // 컨테이너는 클릭 통과
      }}
    >
      {/* <Tooltip title="지표 보기" arrow placement="top"> */}
        <SquareIconButton
          aria-label={isOpen ? '지표 드로어 닫기' : '지표 드로어 열기'}
          aria-expanded={isOpen}
          aria-controls={ariaControlsId}
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          sx={{
            pointerEvents: 'auto', // 버튼만 클릭 가능
          }}
        >
          { isOpen ? <ExpandMoreIcon /> : <ExpandLessIcon /> }
        </SquareIconButton>
      {/* </Tooltip> */}
    </Box>
  );
}